import {
	setChatChannel,
	setChatPanelOpen,
	switchChatChannel,
	upsertChatMessage,
} from "@actions/chat";
import {
	useGetChatChannelsQuery,
	useShareChatMessageMutation,
	useShareLatestCasinoWinMutation,
} from "@api/chatManagement";
import { showAlert } from "@components/alert/action";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	getChatErrorMessage,
	resolvePreferredChatChannelId,
	setStoredChatChannelId,
	useIsLoggedIn,
} from "../utils";

const SHARE_MODE = {
	TRANSACTION: "transaction",
	LATEST_CASINO_WIN: "latest-casino-win",
};

export const useShareToChat = ({ translate }) => {
	const dispatch = useDispatch();
	const isLoggedIn = useIsLoggedIn();
	const activeChannelId = useSelector((state) => state.chat?.activeChannelId);
	const isChatEnabled = useSelector((state) => state.chat?.isChatEnabled !== false);

	const { data: channelsData } = useGetChatChannelsQuery(undefined, {
		skip: !isChatEnabled,
	});

	const [shareMessage, { isLoading: isSharingTransaction }] = useShareChatMessageMutation();
	const [shareLatestCasinoWin, { isLoading: isSharingLatestWin }] =
		useShareLatestCasinoWinMutation();
	const [shareMode, setShareMode] = useState(null);
	const [pendingShare, setPendingShare] = useState(null);
	const [selectedChannelId, setSelectedChannelId] = useState("");

	const isSharing = isSharingTransaction || isSharingLatestWin;

	const enabledChannels = useMemo(
		() => (channelsData?.channels ?? []).filter((channel) => channel.enabled !== false),
		[channelsData?.channels]
	);

	const shareModalTitleKey =
		shareMode === SHARE_MODE.LATEST_CASINO_WIN
			? "chat.shareModal.casinoWinTitle"
			: "chat.shareModal.title";

	useEffect(() => {
		if (!shareMode || !enabledChannels.length) {
			return;
		}

		setSelectedChannelId(resolvePreferredChatChannelId(enabledChannels));
	}, [shareMode, enabledChannels]);

	const openShare = useCallback((payload) => {
		if (!payload?.transactionId || !payload?.shareType) {
			return;
		}

		setShareMode(SHARE_MODE.TRANSACTION);
		setPendingShare(payload);
	}, []);

	const openLatestCasinoWinShare = useCallback(() => {
		setShareMode(SHARE_MODE.LATEST_CASINO_WIN);
		setPendingShare({});
	}, []);

	const closeShare = useCallback(() => {
		setShareMode(null);
		setPendingShare(null);
		setSelectedChannelId("");
	}, []);

	const handleChannelChange = useCallback((channelId) => {
		setSelectedChannelId(channelId);
		setStoredChatChannelId(channelId);
	}, []);

	const confirmShare = useCallback(async () => {
		if (!selectedChannelId || !shareMode) {
			return;
		}

		try {
			let message;

			if (shareMode === SHARE_MODE.LATEST_CASINO_WIN) {
				message = await shareLatestCasinoWin({ channelId: selectedChannelId }).unwrap();
			} else {
				if (!pendingShare?.transactionId || !pendingShare?.shareType) {
					return;
				}

				message = await shareMessage({
					channelId: selectedChannelId,
					transactionId: pendingShare.transactionId,
					shareType: pendingShare.shareType,
				}).unwrap();
			}

			setStoredChatChannelId(selectedChannelId);

			if (isLoggedIn) {
				dispatch(switchChatChannel(activeChannelId, selectedChannelId));
			} else {
				dispatch(setChatChannel(selectedChannelId));
			}

			dispatch(upsertChatMessage(selectedChannelId, message));
			dispatch(setChatPanelOpen(true));
			closeShare();
		} catch (error) {
			dispatch(
				showAlert({
					type: "error",
					msg: getChatErrorMessage(error, translate),
					noTranslate: true,
				})
			);
		}
	}, [
		activeChannelId,
		closeShare,
		dispatch,
		isLoggedIn,
		pendingShare,
		selectedChannelId,
		shareLatestCasinoWin,
		shareMessage,
		shareMode,
		translate,
	]);

	return {
		openShare,
		openLatestCasinoWinShare,
		closeShare,
		confirmShare,
		isSharing,
		isShareModalOpen: Boolean(shareMode),
		shareModalTitleKey,
		enabledChannels,
		selectedChannelId,
		setSelectedChannelId: handleChannelChange,
		canConfirmShare: Boolean(selectedChannelId) && enabledChannels.length > 0,
	};
};
